import React, { useContext, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { SuccessButton } from "../../design-system";
import { typography, borders, surfaces, textColors, interactive } from "../../design-system/tokens";
import { Checkbox, FormField, Input } from "../../design-system/components/inputs";
import { CheckIcon, EyeIcon, EyeOffIcon } from "../../design-system/icons";
import AuthLayout from "./AuthLayout";
import { apiFetch } from "../utils/api";
import { AuthContext, writeAuthProfileCache } from "../context/AuthContext";

/** Only allow same-origin relative return paths (e.g. /affiliate/dashboard/coupons). */
const sanitizeReturnTo = (value) => {
  const raw = String(value || "").trim();
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
};

// Emails auto-generated for provisioned accounts shouldn't be pre-filled as if the user chose them.
const looksLikePlaceholderEmail = (email) => {
  const e = String(email || "").trim();
  if (!e) return true;
  return /placeholder|@example\.|noemail|no-reply|noreply|\.invalid$|\.local$/i.test(e);
};

// Password rules aligned with Register/AccountSettings.
const getPasswordRequirementState = (pwd) => {
  const p = pwd || "";
  const minLength = 8;
  return {
    minLength,
    hasMinLength: p.length >= minLength,
    hasUpperCase: /[A-Z]/.test(p),
    hasLowerCase: /[a-z]/.test(p),
    hasNumbers: /\d/.test(p),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(p),
  };
};

const validatePassword = (pwd) => {
  const { minLength, hasMinLength, hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar } =
    getPasswordRequirementState(pwd);
  if (!hasMinLength) return `Password must be at least ${minLength} characters long`;
  if (!hasUpperCase) return "Password must contain at least one uppercase letter";
  if (!hasLowerCase) return "Password must contain at least one lowercase letter";
  if (!hasNumbers) return "Password must contain at least one number";
  if (!hasSpecialChar) return "Password must contain at least one special character";
  return null;
};

const CompleteAccount = () => {
  const { auth, setAuth, isHydrated } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = sanitizeReturnTo(new URLSearchParams(location.search).get("returnTo"));
  const unauthenticatedLoginPath =
    returnTo && returnTo.startsWith("/partner")
      ? "/partner/login"
      : returnTo && returnTo.startsWith("/affiliate")
        ? "/affiliate/login"
        : "/edit/login";
  const defaultDestinationForUsername = (username) =>
    returnTo || (username ? `/edit/${username}` : "/edit/login");

  const [initialUsername, setInitialUsername] = useState("");
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null); // null | true | false
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Prefill from the profile endpoint (session-authenticated).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await apiFetch("/api/auth/profile/", { suppressUnauthorizedEvent: true });
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        if (cancelled || !data) return;
        const profileUsername = String(data.username || "").trim();
        setInitialUsername(profileUsername);
        setUsername((prev) => prev || profileUsername);
        if (!looksLikePlaceholderEmail(data.email)) {
          setEmail((prev) => prev || String(data.email || "").trim());
        }
      } catch (_) {
        // ignore; the user can type everything manually
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const validateUsername = (value) => {
    const urlSafeRegex = /^[a-zA-Z0-9_-]*$/;
    if (!value) {
      setUsernameError("");
      return true;
    }
    if (!urlSafeRegex.test(value)) {
      setUsernameError("Username can only contain letters, numbers, hyphens (-), and underscores (_)");
      return false;
    }
    if (value.length < 5) {
      setUsernameError("Username must be at least 5 characters long");
      return false;
    }
    if (value.length > 30) {
      setUsernameError("Username must be 30 characters or less");
      return false;
    }
    setUsernameError("");
    return true;
  };

  const usernameChanged = username.trim() !== "" && username.trim() !== initialUsername;

  // Debounced availability check (mirrors Register) — only when the user picks a new username.
  useEffect(() => {
    const urlSafeRegex = /^[a-zA-Z0-9_-]*$/;
    if (!usernameChanged || !urlSafeRegex.test(username) || username.length < 5 || username.length > 30) {
      setCheckingAvailability(false);
      setUsernameAvailable(null);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setCheckingAvailability(true);
      try {
        const res = await apiFetch(`/api/auth/username-available/${encodeURIComponent(username)}/`, {
          method: "GET",
          credentials: "include",
          signal: controller.signal,
        });
        if (res.ok) {
          const data = await res.json();
          setUsernameAvailable(!!data.available);
        } else {
          setUsernameAvailable(null);
        }
      } catch (_) {
        setUsernameAvailable(null);
      } finally {
        setCheckingAvailability(false);
      }
    }, 1000);
    return () => {
      clearTimeout(timer);
      try {
        controller.abort();
      } catch (_) {}
    };
  }, [username, usernameChanged]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (usernameChanged && !validateUsername(username.trim())) {
      setError("Please fix the username errors before submitting");
      return;
    }
    const pwdErr = validatePassword(password);
    if (pwdErr) {
      setPasswordError(pwdErr);
      setError(pwdErr);
      return;
    }
    if (!String(email || "").trim()) {
      setError("Email is required");
      return;
    }
    if (!acceptedLegal) {
      setError("You must agree to the Terms of Use and Privacy Policy before continuing.");
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        new_password: password,
        email: String(email).trim(),
        accepted_legal: true,
      };
      if (usernameChanged) body.username = username.trim();

      const res = await apiFetch("/api/auth/complete-onboarding/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        const serverUser = data.user || {};
        const nextUsername = serverUser.username || (usernameChanged ? username.trim() : auth?.username) || auth?.username;
        writeAuthProfileCache({
          ...serverUser,
          id: serverUser.id || auth?.userId,
          username: nextUsername,
          must_complete_onboarding: false,
        });
        setAuth((prev) => ({
          ...(prev || {}),
          userId: serverUser.id || prev?.userId,
          username: nextUsername,
          email: serverUser.email || String(email).trim(),
          mustCompleteOnboarding: false,
        }));
        navigate(defaultDestinationForUsername(nextUsername), { replace: true });
        return;
      }

      if (data?.field === "email") {
        setError(data?.error || "A user with this email already exists");
      } else if (data?.field === "username") {
        setUsernameAvailable(false);
        setError(data?.error || "This username is taken.");
      } else {
        setError(data?.error || `Could not complete your account (status ${res.status})`);
      }
    } catch (err) {
      setError("Something went wrong. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Unauthenticated visitors have no onboarding to complete.
  if (isHydrated && !(auth && auth.userId)) {
    return <Navigate to={unauthenticatedLoginPath} replace />;
  }
  // Users that already finished onboarding don't belong here.
  if (auth && auth.userId && auth.mustCompleteOnboarding === false) {
    return <Navigate to={defaultDestinationForUsername(auth.username)} replace />;
  }

  const shouldShowPasswordRequirements =
    isPasswordFocused || ((password || "").length > 0 && Boolean(validatePassword(password)));
  const passwordReqState = getPasswordRequirementState(password);
  const passwordRequirements = [
    { key: "minLength", label: `At least ${passwordReqState.minLength} characters long`, met: passwordReqState.hasMinLength },
    { key: "upper", label: "At least one uppercase letter (A-Z)", met: passwordReqState.hasUpperCase },
    { key: "lower", label: "At least one lowercase letter (a-z)", met: passwordReqState.hasLowerCase },
    { key: "number", label: "At least one number (0-9)", met: passwordReqState.hasNumbers },
    { key: "special", label: "At least one special character (e.g. !@#$%^&*)", met: passwordReqState.hasSpecialChar },
  ];

  return (
    <AuthLayout title="Welcome to" subtitle="Finish setting up your account to start building">
      <h2 className={`${typography.h2} ${textColors.primary} mb-6 text-center`}>Complete Your Account</h2>
      {error ? (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-center">{error}</div>
      ) : null}
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField label="New Password" htmlFor="new_password" required>
          <Input
            id="new_password"
            type={isPasswordVisible ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError(validatePassword(e.target.value) || "");
            }}
            onFocus={() => setIsPasswordFocused(true)}
            onBlur={() => setIsPasswordFocused(false)}
            placeholder="Create your password"
            error={passwordError}
            rightAdornment={
              <button
                type="button"
                onMouseDown={(e) => {
                  // Prevent input blur when toggling via mouse (keeps requirements panel stable)
                  e.preventDefault();
                }}
                onClick={() => setIsPasswordVisible((v) => !v)}
                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                aria-pressed={isPasswordVisible}
                className={`h-9 w-9 inline-flex items-center justify-center rounded-md transition-colors ${textColors.muted} ${textColors.hover} focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-500`}
              >
                {isPasswordVisible ? <EyeOffIcon size="sm" /> : <EyeIcon size="sm" />}
              </button>
            }
            required
          />
          {shouldShowPasswordRequirements && (
            <div className={`mt-3 p-3 rounded-md border ${borders.default} ${surfaces.tertiary}`}>
              <p className={`${typography.label} ${textColors.primary} mb-1`}>Password Requirements:</p>
              <ul className={`mt-2 ${typography.caption} ${textColors.secondary} space-y-1`}>
                {passwordRequirements.map((req) => (
                  <li key={req.key} className="flex items-start gap-2">
                    <span className="mt-[2px] inline-flex h-4 w-4 items-center justify-center">
                      {req.met ? (
                        <CheckIcon size="sm" color="success" />
                      ) : (
                        <span className={`h-3 w-3 rounded-full border ${borders.strong} opacity-50`} />
                      )}
                    </span>
                    <span className={req.met ? "text-green-700 dark:text-green-300" : ""}>{req.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </FormField>

        <FormField label="Email Address" htmlFor="email" required>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            required
          />
        </FormField>

        <FormField label="Username" htmlFor="username">
          <Input
            id="username"
            type="text"
            value={username}
            onChange={(e) => {
              const value = e.target.value;
              setUsername(value);
              validateUsername(value);
              setUsernameAvailable(null);
            }}
            placeholder="Keep or change your username"
            error={usernameError}
          />
          {usernameChanged && !usernameError && username.length >= 5 && (
            <div className="mt-2">
              {checkingAvailability && (
                <p className={`${typography.caption} ${textColors.tertiary}`}>Checking availability...</p>
              )}
              {!checkingAvailability && usernameAvailable === true && (
                <p className={`${typography.caption} text-green-600 dark:text-green-400`}>Username is available</p>
              )}
              {!checkingAvailability && usernameAvailable === false && (
                <p className={`${typography.caption} text-red-600 dark:text-red-400`}>This username is taken.</p>
              )}
            </div>
          )}
          {initialUsername && !usernameChanged ? (
            <p className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
              You can keep <span className="font-medium">{initialUsername}</span> or pick a new one.
            </p>
          ) : null}
        </FormField>

        <div className={`relative p-3 rounded-md border ${borders.default} ${surfaces.tertiary}`}>
          <Checkbox
            id="accept_legal"
            checked={acceptedLegal}
            onChange={(e) => setAcceptedLegal(e.target.checked)}
            size="sm"
            label={
              <>
                I agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}>
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className={`underline font-medium ${interactive.link} ${interactive.linkHover}`}>
                  Privacy Policy
                </Link>
              </>
            }
          />
        </div>

        <SuccessButton type="submit" fullWidth loading={submitting}>
          Complete Account
        </SuccessButton>
      </form>
    </AuthLayout>
  );
};

export default CompleteAccount;
